import Layout from "../components/Layout";
import { ArrowUpRight } from "lucide-react";

export default function Projects() {
  const projects = [
    {
      title: "AI for Education in Developing Nations",
      summary:
        "A fine-tuned GPT model trained on IB-aligned datasets to provide scalable academic support for regions with teacher shortages.",
      impact: "4.3× accuracy improvements · 25+ prompt variations · Curriculum-aligned outputs",
      tech: ["GPT Fine-Tuning", "Dataset Engineering", "Evaluation Harness", "Education AI"],
      link: "/projects/ai-education",
      icon: "/project-icons/ai-education.svg",
    },
    {
      title: "AI & Automation Strategy for MOREX Manufacturing",
      summary:
        "A digital transformation roadmap built for a 35-worker factory, introducing AI-vision QC, predictive maintenance and workflow automation.",
      impact: "30–40% defect reduction potential · 5 workflow analyses · Full Industry 4.0 blueprint",
      tech: ["ML Vision", "Predictive Maintenance", "Time-Series Forecasting", "Workflow Automation"],
      link: "/projects/ai-manufacturing-morex",
      icon: "/project-icons/morex-automation.svg",
    },
    {
      title: "I3FY — Interactive Coding Education Platform",
      summary:
        "A modular learning platform that uses guided explanations, interactive examples and a clean UI to help students learn programming.",
      impact: "20+ modules · 50+ examples · Open-source",
      tech: ["React", "Interactive Runner", "Syntax Highlighting", "Education"],
      link: "/projects/i3fy",
      icon: "/project-icons/i3fy.svg",
    },
  ];

  return (
    <Layout>
      <div className="max-w-5xl mx-auto py-14 space-y-12 animate-fadeIn font-inter">

        {/* Header */}
        <header className="text-center space-y-3">
          <p className="text-xs uppercase tracking-[0.3em] opacity-60">
            Build · Innovate · Deliver
          </p>

          <h1 className="text-4xl md:text-5xl font-semibold">
            Selected Project Showcase
          </h1>

          <p className="text-base md:text-lg opacity-70 max-w-2xl mx-auto">
            A curated collection of research, engineering work and product builds demonstrating depth, execution, and real-world impact.
          </p>
        </header>

        {/* Project Grid */}
        <section className="grid gap-6 md:grid-cols-2">
          {projects.map((project) => (
            <article
              key={project.title}
              className="group relative overflow-hidden rounded-3xl border border-black/10 dark:border-white/10 bg-white/20 dark:bg-white/5 backdrop-blur-xl p-6 flex flex-col gap-6 shadow-lg transition-transform duration-300 hover:-translate-y-1 hover:shadow-2xl"
            >
              {/* Thumbnail */}
              <div className="flex justify-center">
                <img
                  src={project.icon}
                  alt={`${project.title} icon`}
                  className="w-20 h-20 opacity-90 group-hover:opacity-100 transition"
                />
              </div>

              {/* Text */}
              <div className="space-y-2">
                <h2 className="text-2xl font-semibold">{project.title}</h2>
                <p className="text-sm opacity-80 leading-relaxed">{project.summary}</p>
                <p className="text-sm opacity-60 leading-relaxed">{project.impact}</p>
              </div>

              {/* Tech Pills */}
              <div className="flex flex-wrap gap-2 text-xs uppercase tracking-wide opacity-80">
                {project.tech.map((tool) => (
                  <span
                    key={`${project.title}-${tool}`}
                    className="rounded-full border border-black/20 dark:border-white/20 px-3 py-1 bg-white/30 dark:bg-white/10"
                  >
                    {tool}
                  </span>
                ))}
              </div>

              {/* View Project */}
              <div className="mt-auto pt-2">
                <a
                  href={project.link}
                  className="inline-flex items-center gap-2 text-sm font-medium text-black/70 dark:text-white/80 hover:text-black dark:hover:text-white transition"
                >
                  View project
                  <ArrowUpRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </a>
              </div>

              {/* Hover Glow */}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/0 via-white/0 to-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </article>
          ))}
        </section>
      </div>
    </Layout>
  );
}
